import QrScanner from "qr-scanner";
import { CONFIG, ELEMENT_IDS } from "../config";
import type { CameraInfo, QRData, VideoConstraints } from "../types";

export class CameraManager {
  private qrScanner: QrScanner | null = null;
  private localStream: MediaStream | null = null;
  private availableCameras: CameraInfo[] = [];
  private currentCameraIndex: number = 0;
  private selectedCameraId: string | null = null;
  private currentMode: "qr-scanning" | "video-streaming" | null = null;

  async getAvailableCameras(): Promise<CameraInfo[]> {
    try {
      this.availableCameras = await QrScanner.listCameras(true);
      return this.availableCameras;
    } catch (error) {
      this.availableCameras = [];
      return [];
    }
  }

  async startQRScanner(onScan: (data: QRData) => void): Promise<void> {
    const video = document.getElementById(ELEMENT_IDS.SCANNER_VIDEO) as HTMLVideoElement;

    await this.getAvailableCameras();

    // Find environment/back camera
    const backCamera = this.availableCameras.find(
      (camera) =>
        camera.label.toLowerCase().includes("back") ||
        camera.label.toLowerCase().includes("environment") ||
        camera.label.toLowerCase().includes("rear")
    );

    // Set initial camera index
    if (backCamera) {
      this.currentCameraIndex = this.availableCameras.findIndex((cam) => cam.id === backCamera.id);
      this.selectedCameraId = backCamera.id;
    }

    this.qrScanner = new QrScanner(
      video,
      (result) => {
        try {
          const data = JSON.parse(result.data);
          onScan(data);
        } catch (error) {
          // Silent error handling
        }
      },
      {
        highlightScanRegion: true,
        highlightCodeOutline: true,
        preferredCamera: backCamera?.id || "environment",
        maxScansPerSecond: CONFIG.QR.MAX_SCANS_PER_SECOND,
      }
    );

    await this.qrScanner.start();
  }

  stopQRScanner(): void {
    if (this.qrScanner) {
      this.qrScanner.stop();
      this.qrScanner.destroy();
      this.qrScanner = null;
    }
  }

  // =============================================================================
  // CAMERA STREAM METHODS - Simplified with built-in fallbacks
  // =============================================================================

  async getQRScanningStream(): Promise<MediaStream> {
    this.currentMode = "qr-scanning";

    // QR scanning constraint sets with built-in fallbacks
    const constraintSets = [
      // Primary: 720p with selected camera
      this.buildConstraints({ width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }),
      // Fallback: 720p with any environment camera
      {
        facingMode: { exact: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      },
      // Fallback: 720p with flexible facing mode
      { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
      // Last resort: 480p
      { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 15 } },
    ];

    return await this.tryConstraintSets(constraintSets, "QR scanning");
  }

  async getVideoStreamingStream(): Promise<MediaStream> {
    this.currentMode = "video-streaming";

    // Video streaming constraint sets with built-in fallbacks
    const constraintSets = [
      // Primary: 4K with selected camera
      this.buildConstraints({ width: { ideal: 3840 }, height: { ideal: 2160 }, frameRate: { ideal: 30 } }),
      // Fallback: 1080p with selected camera
      this.buildConstraints({ width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }),
      // Fallback: 720p with selected camera
      this.buildConstraints({ width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }),
      // Fallback: 720p with any environment camera
      {
        facingMode: { exact: "environment" },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
      },
      // Last resort: 720p with flexible facing mode
      { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    ];

    return await this.tryConstraintSets(constraintSets, "video streaming");
  }

  // =============================================================================
  // INTERNAL METHODS - Simplified constraint handling
  // =============================================================================

  private buildConstraints(baseConstraints: VideoConstraints): VideoConstraints {
    if (this.selectedCameraId) {
      return {
        deviceId: { exact: this.selectedCameraId },
        facingMode: "environment",
        ...baseConstraints,
      };
    }

    return {
      facingMode: { exact: "environment" },
      ...baseConstraints,
    };
  }

  private async tryConstraintSets(constraintSets: VideoConstraints[], purpose: string): Promise<MediaStream> {
    for (let i = 0; i < constraintSets.length; i++) {
      const constraints = constraintSets[i];

      try {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          video: constraints,
        });

        return this.localStream;
      } catch (error) {
        // If this was the last attempt, throw the error
        if (i === constraintSets.length - 1) {
          throw new Error(`All ${purpose} constraint sets failed`);
        }
      }
    }

    throw new Error(`Unexpected error in ${purpose} constraint handling`);
  }

  async switchCamera(peerConnection?: RTCPeerConnection): Promise<void> {
    if (this.availableCameras.length <= 1) return;

    try {
      // Cycle to next camera
      this.currentCameraIndex = (this.currentCameraIndex + 1) % this.availableCameras.length;
      const nextCamera = this.availableCameras[this.currentCameraIndex];
      this.selectedCameraId = nextCamera.id;

      if (this.qrScanner) {
        // Switch camera during QR scanning
        await this.qrScanner.setCamera(nextCamera.id);
      } else if (this.localStream && this.currentMode) {
        // Switch camera during streaming
        const oldStream = this.localStream;

        // Get new stream with appropriate constraints for current mode
        if (this.currentMode === "video-streaming") {
          await this.getVideoStreamingStream();
        } else {
          await this.getQRScanningStream();
        }

        // Update UI video element
        this.setupCameraStreamVideo();

        // Replace WebRTC track if peer connection provided
        if (peerConnection && this.localStream) {
          await this.replaceWebRTCTrack(peerConnection, oldStream);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  private async replaceWebRTCTrack(peerConnection: RTCPeerConnection, oldStream: MediaStream): Promise<void> {
    if (!this.localStream) return;

    const newVideoTrack = this.localStream.getVideoTracks()[0];
    if (!newVideoTrack) {
      throw new Error("No video track in new stream");
    }

    // Find and replace the video track in peer connection
    const senders = peerConnection.getSenders();
    const videoSender = senders.find((sender) => sender.track?.kind === "video");

    if (videoSender) {
      await videoSender.replaceTrack(newVideoTrack);
    }

    // Stop old video tracks
    oldStream.getVideoTracks().forEach((track) => track.stop());
  }

  // =============================================================================
  // DEPRECATED METHODS - Use switchCamera() instead
  // =============================================================================

  async replaceVideoTrack(peerConnection: RTCPeerConnection, newCameraId: string): Promise<void> {
    // Find the camera index for the new camera ID
    const cameraIndex = this.availableCameras.findIndex((cam) => cam.id === newCameraId);
    if (cameraIndex === -1) {
      throw new Error(`Camera with ID ${newCameraId} not found`);
    }

    // Update current camera index and switch
    this.currentCameraIndex = cameraIndex;
    await this.switchCamera(peerConnection);
  }

  setupCameraStreamVideo(): void {
    const scannerVideo = document.getElementById(ELEMENT_IDS.SCANNER_VIDEO) as HTMLVideoElement;
    const cameraStreamVideo = document.getElementById(ELEMENT_IDS.CAMERA_STREAM_VIDEO) as HTMLVideoElement;

    if (scannerVideo && cameraStreamVideo && this.localStream) {
      // Hide scanner video and show camera stream video
      scannerVideo.style.display = "none";
      cameraStreamVideo.style.display = "block";

      // Set the stream to the camera video element
      cameraStreamVideo.srcObject = this.localStream;
      cameraStreamVideo.style.objectFit = "cover";
      cameraStreamVideo.setAttribute("playsinline", "true");
      cameraStreamVideo.setAttribute("autoplay", "true");

      // Start playing the video
      cameraStreamVideo.play().catch((error) => {
        // Silent error handling
      });
    }
  }

  updateSwitchCameraButtonVisibility(): void {
    const switchCameraBtn = document.getElementById(ELEMENT_IDS.SWITCH_CAMERA_BTN);
    if (switchCameraBtn) {
      switchCameraBtn.style.display = this.availableCameras.length > 1 ? "block" : "none";
    }
  }

  cleanup(): void {
    this.stopQRScanner();

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    this.currentCameraIndex = 0;
    this.availableCameras = [];
    this.selectedCameraId = null;
    this.currentMode = null;
  }

  get hasMultipleCameras(): boolean {
    return this.availableCameras.length > 1;
  }

  get currentStream(): MediaStream | null {
    return this.localStream;
  }

  get currentCameraId(): string | null {
    return this.selectedCameraId;
  }

  // =============================================================================
  // DEPRECATED METHODS - For backward compatibility, will be removed
  // =============================================================================

  async getUserMediaWithConstraints(constraints: VideoConstraints): Promise<MediaStream> {
    return await this.tryConstraintSets([constraints], "legacy");
  }
}
